import React, { useCallback, useEffect, useState } from 'react';
import HistoryIcon from '@mui/icons-material/History';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { portfolioService } from '../../api';
import { CARD_BORDER } from '../../components/Section';
import COLORS from '../../constants/colors';
import { PortfolioMinimumConfigHistoryEntry } from '../../types/portfolio';
import { formatDateTime } from '../../utils/date';

const PAGE_SIZE = 10;

const FIELD_LABELS: Record<string, string> = {
  basicSubscriptionFloor: 'Basic minimum subscription',
  premiumSubscriptionFloor: 'Premium minimum subscription',
};

const tableSx = {
  '& .MuiTableCell-root': { fontFamily: 'inherit', fontSize: '0.8125rem' },
};

const headerCellSx = { color: COLORS.LIGHT_GRAY, fontWeight: 600 };

/** A stored floor as the admin reads it: an amount in SAR, or "No floor" when unset or 0. */
const describeValue = (value: string | null): string => {
  const amount = value === null || value === '' ? 0 : Number(value);

  if (!Number.isFinite(amount)) {
    return value ?? '';
  }

  return amount > 0
    ? `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SAR`
    : 'No floor';
};

const describeEmployee = (entry: PortfolioMinimumConfigHistoryEntry): string => {
  if (entry.employeeEmail) {
    return entry.employeeEmail;
  }

  return entry.employeeId != null ? `Employee #${entry.employeeId}` : '—';
};

interface PortfolioMinimumConfigHistoryProps {
  portfolioId: number;
  /** Changes after every save, so the newest entries show at once. */
  reloadKey?: string | number;
}

/**
 * MAD-5480 (MAD-5449 AC-05): who changed the Basic or Premium minimum subscription, when, and from what to
 * what — one row per changed floor, newest first.
 */
export function PortfolioMinimumConfigHistory({ portfolioId, reloadKey }: PortfolioMinimumConfigHistoryProps) {
  const [entries, setEntries] = useState<PortfolioMinimumConfigHistoryEntry[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await portfolioService.fetchMinimumConfigHistory(portfolioId, { offset: 0, limit: PAGE_SIZE });

      setEntries(response.data.items);
      setTotalItems(response.data.totalItems);
    } catch (loadError: any) {
      setError(loadError?.message || 'Failed to load the change history');
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage, reloadKey]);

  const loadMore = async () => {
    setLoadingMore(true);

    try {
      const response = await portfolioService.fetchMinimumConfigHistory(portfolioId, {
        offset: entries.length,
        limit: PAGE_SIZE,
      });

      setEntries((current) => [...current, ...response.data.items]);
      setTotalItems(response.data.totalItems);
    } catch (loadError: any) {
      setError(loadError?.message || 'Failed to load the change history');
    } finally {
      setLoadingMore(false);
    }
  };

  const renderBody = () => {
    if (loading && !entries.length) {
      return <Skeleton variant="rectangular" width="100%" height={96} />;
    }

    if (!entries.length) {
      return (
        <Typography sx={{ px: '16px', py: '14px', fontSize: '0.8125rem', color: COLORS.LIGHT_GRAY }}>
          No changes yet. These settings still have their starting values.
        </Typography>
      );
    }

    return (
      <Table size="small" sx={tableSx}>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>Date & time</TableCell>
            <TableCell sx={headerCellSx}>Changed by</TableCell>
            <TableCell sx={headerCellSx}>Setting</TableCell>
            <TableCell sx={headerCellSx}>From</TableCell>
            <TableCell sx={headerCellSx}>To</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(entry.createdAt)}</TableCell>
              <TableCell>{describeEmployee(entry)}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{FIELD_LABELS[entry.field] ?? entry.field}</TableCell>
              <TableCell sx={{ color: COLORS.DARK_GREY }}>{describeValue(entry.oldValue)}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{describeValue(entry.newValue)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${CARD_BORDER}`, overflow: 'hidden' }}>
      <Box
        sx={{
          px: '16px',
          py: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: COLORS.X_LIGHT_CYAN,
          borderBottom: `1px solid ${CARD_BORDER}`,
        }}
      >
        <HistoryIcon sx={{ fontSize: 18, color: COLORS.DARK_GREY }} />
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: COLORS.MAIN_DARK }}>
            Change history
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: COLORS.DARK_GREY }}>
            Every saved change, with who made it and when — newest first
          </Typography>
        </Box>
      </Box>
      {error && (
        <Alert severity="error" sx={{ m: '12px', borderRadius: '10px' }}>
          {error}
        </Alert>
      )}
      {renderBody()}
      {entries.length < totalItems && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: '10px', borderTop: `1px solid ${CARD_BORDER}` }}>
          <LoadingButton size="small" loading={loadingMore} onClick={loadMore}>
            Show {Math.min(PAGE_SIZE, totalItems - entries.length)} more
          </LoadingButton>
        </Box>
      )}
    </Paper>
  );
}
